import {Injectable, NgZone, OnDestroy, signal} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Router} from '@angular/router';
import {PluginListenerHandle} from '@capacitor/core';
import {pushPlatform} from './push-platform';
import {distinctUntilChanged, firstValueFrom, map, skip, Subscription, timeout} from 'rxjs';
import {AuthService} from '../auth/auth.service';
import {ApiResponse} from '../auth/auth.models';
import {environment} from '../../environments/environment';
import {buildApiUrl} from '../shared/api/api-url';
import {AppLanguageService} from '../shared/i18n/app-language.service';
import {buildPlatformAwareRoutePathname, normalizeAppLanguageCode} from '../shared/i18n/app-language';
import {PushLocalStateService} from './push-local-state.service';
import {PushRegistrationDto} from './push-registration-dto';
import {PushRegistrationRequestDto} from './push-registration-request-dto';
import {isPushSupported, PushNative} from './push-native';

@Injectable({providedIn: 'root'})
export class PushNotificationsService implements OnDestroy {
  readonly supported = isPushSupported();
  readonly state = signal('disabled');
  readonly busy = signal(false);
  readonly optedIn = signal(false);
  readonly registrationId = signal<string | null>(null);
  private listeners: PluginListenerHandle[] = [];
  private sessionSubscription?: Subscription;
  private initialization?: Promise<void>;
  private token: string | null = null;
  private resuming: Promise<void> = Promise.resolve();
  private syncing: Promise<void> = Promise.resolve();

  constructor(
    private readonly auth: AuthService, private readonly local: PushLocalStateService,
    private readonly http: HttpClient, private readonly router: Router,
    private readonly language: AppLanguageService, private readonly zone: NgZone,
  ) {}

  initialize(): Promise<void> {
    if (!this.supported) return Promise.resolve();
    this.initialization ??= this.initializeCore();
    return this.initialization;
  }

  async enable(): Promise<void> {
    if (this.busy() || !this.auth.session) return;
    const userId = this.auth.session.user.id;
    this.busy.set(true);
    try {
      await this.initialize();
      await this.local.markPermissionRequested();
      const permission = await pushPlatform.requestPermissions();
      if (permission.receive !== 'granted') { this.state.set('denied'); return; }
      if (this.auth.session?.user.id !== userId || !this.auth.isAuthenticated()) return;
      if (!await this.local.enable(userId)) return;
      this.optedIn.set(true);
      this.state.set('registering');
      await pushPlatform.register();
    } finally { this.busy.set(false); }
  }

  async disable(): Promise<void> {
    this.busy.set(true);
    try {
      await this.local.disable(this.auth.session?.user.id, 'preference');
      this.registrationId.set(null);
      this.optedIn.set(false);
      this.state.set('disabled');
    } finally { this.busy.set(false); }
  }

  openSettings(): Promise<void> { return PushNative.openSettings(); }

  ngOnDestroy(): void {
    this.sessionSubscription?.unsubscribe();
    for (const listener of this.listeners) void listener.remove();
    this.listeners = [];
  }

  private async initializeCore(): Promise<void> {
    this.listeners.push(await PushNative.addListener('tap', () => {
      void this.resume().catch(() => this.state.set('error'));
    }));
    this.listeners.push(await pushPlatform.addListener('registration', token => {
      this.token = token.value;
      this.queueSync();
    }));
    this.listeners.push(await pushPlatform.addListener('registrationError', () => {
      this.zone.run(() => this.state.set('error'));
    }));
    this.listeners.push(await pushPlatform.addAppListener('appStateChange', event => {
      if (event.isActive) void this.resume().catch(() => this.state.set('error'));
    }));
    this.sessionSubscription = this.auth.session$.pipe(map(session => session?.user.id), distinctUntilChanged(), skip(1)).subscribe(userId => {
      // A null session may mean expiry. Never interpret it as an explicit logout.
      if (userId) {
        this.local.startSession();
        void this.resume().catch(() => this.state.set('error'));
      }
    });
    await this.resume();
  }

  private resume(): Promise<void> {
    const next = this.resuming.then(() => this.resumeCore());
    this.resuming = next.catch(() => undefined);
    return next;
  }

  private async resumeCore(): Promise<void> {
    const session = this.auth.session;
    const previous = await this.local.read();
    if (previous?.enabled && (!session || previous.userId === session.user.id)) {
      await this.handleTap(previous.bindingId);
    }
    if (!session || !this.auth.isAuthenticated()) return;
    await this.local.prepareAccount(session.user.id);
    this.registrationId.set(null);
    const wanted = await this.local.wantsPush(session.user.id);
    this.optedIn.set(wanted);
    if (!wanted) { this.state.set('disabled'); return; }
    if (!await this.ensurePermission()) return;
    if (this.auth.session?.user.id !== session.user.id || !this.auth.isAuthenticated()) return;
    const installation = await this.local.activate(session.user.id);
    if (!installation) { this.optedIn.set(false); this.state.set('disabled'); return; }
    this.state.set('registering');
    await pushPlatform.register();
  }

  private async ensurePermission(): Promise<boolean> {
    let permission = await pushPlatform.checkPermissions();
    if (permission.receive === 'prompt' && await this.local.shouldRequestPermission()) {
      await this.local.markPermissionRequested();
      permission = await pushPlatform.requestPermissions();
    }
    if (permission.receive === 'granted') return true;
    await this.local.disable(this.auth.session?.user.id, 'permission');
    this.state.set('denied');
    return false;
  }

  private queueSync(): void {
    this.syncing = this.syncing.then(() => this.synchronize()).catch(() => {
      this.zone.run(() => this.state.set('error'));
    });
  }

  private async synchronize(): Promise<void> {
    const installation = await this.local.read();
    if (!installation?.enabled || !this.token || !this.auth.isAuthenticated()) return;
    if (installation.userId !== this.auth.session?.user.id) return;
    const request: PushRegistrationRequestDto = {
      installationId: installation.installationId, bindingId: installation.bindingId,
      installationSecret: installation.installationSecret, token: this.token,
      locale: normalizeAppLanguageCode(this.language.languageCode) ?? 'en',
      appVersion: environment.appVersion,
    };
    const result = await firstValueFrom(this.http.put<ApiResponse<PushRegistrationDto>>(
      buildApiUrl('/v1/push/registrations'), request).pipe(timeout(15000)));
    const current = await this.local.read();
    if (!current?.enabled || current.bindingId !== installation.bindingId) {
      return;
    }
    if (!result.success || !result.data) throw new Error('Push registration failed.');
    this.zone.run(() => {
      this.registrationId.set(result.data!.registrationId);
      this.state.set(result.data!.enabled ? 'enabled' : 'disabled');
    });
  }

  private async handleTap(bindingId: string): Promise<void> {
    const tap = await PushNative.takeTap();
    if (tap.bindingId !== bindingId || !tap.route) return;
    if (!['/home', '/people/trusted-contacts', '/inventory/containers', '/basket',
      '/reports/expiring', '/reports/expired'].includes(tap.route)) return;
    const target = buildPlatformAwareRoutePathname(tap.route, this.language.languageCode);
    await this.zone.run(() => this.auth.isAuthenticated()
      ? this.router.navigateByUrl(target)
      : this.router.navigate([buildPlatformAwareRoutePathname('/login', this.language.languageCode)],
        {queryParams: {returnUrl: target}}));
  }
}
